import { createRouter, createWebHashHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import type { App } from 'vue'
import { Layout, getParentLayout } from '@/utils/routerHelper'
import { useI18n } from '@/hooks/web/useI18n'
import { NO_RESET_WHITE_LIST } from '@/constants'

const { t } = useI18n()

export const constantRouterMap: AppRouteRecordRaw[] = [
  {
    path: '/',
    component: Layout,
    redirect: '/dashboard/analysis',
    name: 'Root',
    meta: {
      hidden: true
    }
  },
  {
    path: '/redirect',
    component: Layout,
    name: 'RedirectWrap',
    children: [
      {
        path: '/redirect/:path(.*)',
        name: 'Redirect',
        component: () => import('@/views/Redirect/Redirect.vue'),
        meta: {}
      }
    ],
    meta: {
      hidden: true,
      noTagsView: true
    }
  },
  {
    path: '/login',
    component: () => import('@/views/Login/Login.vue'),
    name: 'Login',
    meta: {
      hidden: true,
      title: t('router.login'),
      noTagsView: true
    }
  },
  {
    path: '/personal',
    component: Layout,
    redirect: '/personal/personal-center',
    name: 'Personal',
    meta: {
      title: t('router.personal'),
      hidden: true,
      canTo: true
    },
    children: [
      {
        path: 'personal-center',
        component: () => import('@/views/Personal/PersonalCenter/PersonalCenter.vue'),
        name: 'PersonalCenter',
        meta: {
          title: t('router.personalCenter'),
          hidden: true,
          canTo: true
        }
      }
    ]
  },
  {
    path: '/404',
    component: () => import('@/views/Error/404.vue'),
    name: 'NoFind',
    meta: {
      hidden: true,
      title: '404',
      noTagsView: true
    }
  }
]

export const asyncRouterMap: AppRouteRecordRaw[] = [
  {
    path: '/dashboard',
    component: Layout,
    redirect: '/dashboard/analysis',
    name: 'Dashboard',
    meta: {
      title: t('router.dashboard'),
      icon: 'vi-ant-design:dashboard-filled',
      alwaysShow: true
    },
    children: [
      {
        path: 'analysis',
        component: () => import('@/views/Dashboard/Analysis.vue'),
        name: 'Analysis',
        meta: {
          title: t('router.analysis'),
          noCache: true,
          affix: true
        }
      },
      {
        path: 'workplace',
        component: () => import('@/views/Dashboard/Workplace.vue'),
        name: 'Workplace',
        meta: {
          title: t('router.workplace'),
          noCache: true
        }
      }
    ]
  },
  {
    path: '/sms',
    component: Layout,
    redirect: '/sms/list',
    name: 'SmsCenter',
    meta: {
      title: '短信管理',
      icon: 'vi-ep:message'
    },
    children: [
      {
        path: 'list',
        component: () => import('@/views/Sms/Index.vue'),
        name: 'SmsList',
        meta: {
          title: '短信管理',
          icon: 'vi-ep:message'
        }
      }
    ]
  },
  {
    path: '/operation/ad',
    component: Layout,
    name: 'AdCenter',
    meta: {
      title: '广告管理',
      icon: 'vi-ep:promotion'
    },
    children: [
      {
        path: '',
        component: () => import('@/views/Ad/Ad.vue'),
        name: 'AdList',
        meta: {
          // 查询条件随 URL 重建，避免不同地址的缓存实例互相恢复旧条件。
          noCache: true,
          title: '广告管理',
          icon: 'vi-ep:promotion'
        }
      },
      {
        path: 'add',
        component: () => import('@/views/Ad/WritePage.vue'),
        name: 'AdAdd',
        meta: {
          title: '新增广告',
          noTagsView: true,
          noCache: true,
          hidden: true,
          canTo: true,
          activeMenu: '/operation/ad'
        }
      },
      {
        path: 'detail/:adId',
        component: () => import('@/views/Ad/DetailPage.vue'),
        name: 'AdDetail',
        meta: {
          title: '广告详情',
          hidden: true,
          canTo: true,
          noCache: true,
          noTagsView: true,
          activeMenu: '/operation/ad'
        }
      },
      {
        path: 'edit/:adId',
        component: () => import('@/views/Ad/WritePage.vue'),
        name: 'AdEdit',
        meta: {
          title: '编辑广告',
          noTagsView: true,
          noCache: true,
          hidden: true,
          canTo: true,
          activeMenu: '/operation/ad'
        }
      }
    ]
  },
  {
    path: '/user',
    component: Layout,
    redirect: '/user/list',
    name: 'CUser',
    meta: {
      title: '用户管理',
      icon: 'vi-ep:user'
    },
    children: [
      {
        path: 'list',
        component: () => import('@/views/User/Index.vue'),
        name: 'CUserList',
        meta: {
          title: '用户管理',
          icon: 'vi-ep:user'
        }
      },
      {
        path: 'edit/:userId',
        component: () => import('@/views/User/WritePage.vue'),
        name: 'CUserEdit',
        meta: {
          title: '编辑用户',
          noTagsView: true,
          noCache: true,
          hidden: true,
          canTo: true,
          activeMenu: '/user/list'
        }
      },
      {
        path: 'detail/:userId',
        component: () => import('@/views/User/DetailPage.vue'),
        name: 'CUserDetail',
        meta: {
          title: '用户详情',
          noTagsView: true,
          noCache: true,
          hidden: true,
          canTo: true,
          activeMenu: '/user/list'
        }
      }
    ]
  },
  {
    path: '/company',
    component: Layout,
    redirect: '/company/list',
    name: 'Company',
    meta: {
      title: '企业管理',
      icon: 'vi-ep:office-building'
    },
    children: [
      {
        path: 'list',
        component: () => import('@/views/Company/List/Index.vue'),
        name: 'CompanyList',
        meta: {
          title: '企业管理',
          icon: 'vi-ep:office-building'
        }
      },
      {
        path: 'business-role-type',
        redirect: '/operation/business-role-type',
        name: 'CompanyBusinessRoleTypeRedirect',
        meta: { hidden: true, noTagsView: true }
      },
      {
        path: 'edit/:companyId',
        component: () => import('@/views/Company/WritePage.vue'),
        name: 'CompanyEdit',
        meta: {
          title: '编辑企业',
          noTagsView: true,
          noCache: true,
          hidden: true,
          canTo: true,
          activeMenu: '/company/list'
        }
      },
      {
        path: 'detail/:companyId',
        component: () => import('@/views/Company/DetailPage.vue'),
        name: 'CompanyDetail',
        meta: {
          title: '企业详情',
          noTagsView: true,
          noCache: true,
          hidden: true,
          canTo: true,
          activeMenu: '/company/list'
        }
      },
      {
        path: 'detail-v2/:companyId',
        component: () => import('@/views/Company/DetailPageV2.vue'),
        name: 'CompanyDetailV2',
        meta: {
          title: '企业详情',
          noTagsView: true,
          noCache: true,
          hidden: true,
          canTo: true,
          activeMenu: '/company/list'
        }
      }
    ]
  },
  {
    path: '/content',
    component: Layout,
    redirect: '/content/company-goods',
    name: 'Content',
    meta: {
      title: '内容审核',
      icon: 'vi-ep:document',
      alwaysShow: true
    },
    children: [
      {
        path: 'realname-request',
        alias: '/user/realname-request',
        component: () => import('@/views/User/RealnameVerifyRequest/Index.vue'),
        name: 'UserRealnameVerifyRequest',
        meta: {
          title: '实名认证审核',
          activeMenu: '/content/realname-request'
        }
      },
      {
        path: 'realname-request/detail/:id',
        alias: '/user/realname-request/detail/:id',
        component: () => import('@/views/User/RealnameVerifyRequest/DetailPage.vue'),
        name: 'UserRealnameVerifyRequestDetail',
        meta: {
          title: '实名认证审核详情',
          noTagsView: true,
          noCache: true,
          hidden: true,
          canTo: true,
          activeMenu: '/content/realname-request'
        }
      },
      {
        path: 'company-bind-request',
        alias: '/company/bind-request',
        component: () => import('@/views/Company/BindRequest/Index.vue'),
        name: 'CompanyBindRequest',
        meta: {
          title: '企业绑定审核',
          activeMenu: '/content/company-bind-request'
        }
      },
      {
        path: 'company-bind-request/detail/:id',
        alias: '/company/bind-request/detail/:id',
        component: () => import('@/views/Company/BindRequest/DetailPage.vue'),
        name: 'CompanyBindRequestDetail',
        meta: {
          title: '企业绑定审核详情',
          noTagsView: true,
          noCache: true,
          hidden: true,
          canTo: true,
          activeMenu: '/content/company-bind-request'
        }
      },
      {
        path: 'company-verify-request',
        alias: '/company/verify-request',
        component: () => import('@/views/Company/VerifyRequest/Index.vue'),
        name: 'CompanyVerifyRequest',
        meta: {
          title: '企业认证审核',
          activeMenu: '/content/company-verify-request'
        }
      },
      {
        path: 'company-verify-request/detail/:id',
        alias: '/company/verify-request/detail/:id',
        component: () => import('@/views/Company/VerifyRequest/DetailPage.vue'),
        name: 'CompanyVerifyRequestDetail',
        meta: {
          title: '企业认证审核详情',
          noTagsView: true,
          noCache: true,
          hidden: true,
          canTo: true,
          activeMenu: '/content/company-verify-request'
        }
      },
      {
        path: 'company-business-role',
        alias: '/company/business-role',
        component: () => import('@/views/Company/BusinessRole/Index.vue'),
        name: 'CompanyBusinessRole',
        meta: {
          title: '经营身份审核',
          activeMenu: '/content/company-business-role'
        }
      },
      {
        path: 'company-business-role/detail/:companyId',
        alias: '/company/business-role/detail/:companyId',
        component: () => import('@/views/Company/BusinessRole/DetailPage.vue'),
        name: 'CompanyBusinessRoleDetail',
        meta: {
          title: '经营身份审核详情',
          noTagsView: true,
          noCache: true,
          hidden: true,
          canTo: true,
          activeMenu: '/content/company-business-role'
        }
      },
      {
        path: 'feedback',
        alias: '/user/feedback',
        component: () => import('@/views/Content/Feedback/Index.vue'),
        name: 'Feedback',
        meta: {
          title: '用户反馈',
          activeMenu: '/content/feedback'
        }
      },
      {
        path: 'feedback/detail/:feedbackId',
        alias: '/user/feedback/detail/:feedbackId',
        component: () => import('@/views/Content/Feedback/DetailPage.vue'),
        name: 'FeedbackDetail',
        meta: {
          title: '反馈详情',
          noTagsView: true,
          noCache: true,
          hidden: true,
          canTo: true,
          activeMenu: '/content/feedback'
        }
      },
      {
        path: 'company-goods',
        component: () => import('@/views/Content/CompanyGoods/Index.vue'),
        name: 'CompanyGoods',
        meta: {
          title: '商品管理'
        }
      },
      {
        path: 'company-goods/add',
        component: () => import('@/views/Content/CompanyGoods/WritePage.vue'),
        name: 'CompanyGoodsAdd',
        meta: {
          title: '新增商品',
          hidden: true,
          canTo: true,
          noTagsView: true,
          noCache: true,
          activeMenu: '/content/company-goods'
        }
      },
      {
        path: 'company-goods/edit/:goodsId',
        component: () => import('@/views/Content/CompanyGoods/WritePage.vue'),
        name: 'CompanyGoodsEdit',
        meta: {
          title: '编辑商品',
          hidden: true,
          canTo: true,
          noTagsView: true,
          noCache: true,
          activeMenu: '/content/company-goods'
        }
      },
      {
        path: 'company-goods/detail/:goodsId',
        component: () => import('@/views/Content/CompanyGoods/DetailPage.vue'),
        name: 'CompanyGoodsDetail',
        meta: {
          title: '商品详情',
          noTagsView: true,
          noCache: true,
          hidden: true,
          canTo: true,
          activeMenu: '/content/company-goods'
        }
      },
      {
        path: 'goods-class',
        component: () => import('@/views/CompanyGoodsClass/Index.vue'),
        name: 'CompanyGoodsClass',
        meta: {
          title: '商品分类管理'
        }
      },
      {
        path: 'goods-class/add',
        component: () => import('@/views/CompanyGoodsClass/WritePage.vue'),
        name: 'CompanyGoodsClassAdd',
        meta: {
          title: '新增商品分类',
          noTagsView: true,
          noCache: true,
          hidden: true,
          canTo: true,
          activeMenu: '/content/goods-class'
        }
      },
      {
        path: 'goods-class/edit/:classId',
        component: () => import('@/views/CompanyGoodsClass/WritePage.vue'),
        name: 'CompanyGoodsClassEdit',
        meta: {
          title: '编辑商品分类',
          noTagsView: true,
          noCache: true,
          hidden: true,
          canTo: true,
          activeMenu: '/content/goods-class'
        }
      },
      {
        path: 'goods-class/detail/:classId',
        component: () => import('@/views/CompanyGoodsClass/DetailPage.vue'),
        name: 'CompanyGoodsClassDetail',
        meta: {
          title: '商品分类详情',
          noTagsView: true,
          noCache: true,
          hidden: true,
          canTo: true,
          activeMenu: '/content/goods-class'
        }
      },
      {
        path: 'post-comment',
        component: () => import('@/views/Content/PostComment/Index.vue'),
        name: 'PostComment',
        meta: {
          title: '评论管理',
          noCache: true
        }
      },
      {
        path: 'post-comment/detail/:postCommentId',
        component: () => import('@/views/Content/PostComment/DetailPage.vue'),
        name: 'PostCommentDetail',
        meta: {
          title: '评论详情',
          noTagsView: true,
          noCache: true,
          hidden: true,
          canTo: true,
          activeMenu: '/content/post-comment'
        }
      },
      {
        path: 'post',
        component: () => import('@/views/Post/Index.vue'),
        name: 'Post',
        meta: {
          title: '供求帖子管理'
        }
      },
      {
        path: 'post/detail/:postId',
        component: () => import('@/views/Post/DetailPage.vue'),
        name: 'PostDetail',
        meta: {
          title: '帖子详情',
          noTagsView: true,
          noCache: true,
          hidden: true,
          canTo: true,
          activeMenu: '/content/post'
        }
      },
      {
        path: 'post/edit/:postId',
        component: () => import('@/views/Post/WritePage.vue'),
        name: 'PostEdit',
        meta: {
          title: '编辑帖子',
          noTagsView: true,
          noCache: true,
          hidden: true,
          canTo: true,
          activeMenu: '/content/post'
        }
      }
    ]
  },
  {
    path: '/product',
    component: Layout,
    redirect: '/product/list',
    name: 'ProductCenter',
    meta: {
      title: '产品中心',
      icon: 'vi-ep:goods',
      alwaysShow: true
    },
    children: [
      {
        path: 'list',
        component: () => import('@/views/Product/Index.vue'),
        name: 'Product',
        meta: {
          title: '产品管理'
        }
      },
      {
        path: 'class',
        component: () => import('@/views/ProductClass/Index.vue'),
        name: 'ProductClass',
        meta: {
          title: '产品分类管理'
        }
      },
      {
        path: 'attr',
        component: () => import('@/views/GoodsAttr/Index.vue'),
        name: 'GoodsAttr',
        meta: {
          title: '商品属性管理'
        }
      },
      {
        path: 'list/add',
        component: () => import('@/views/Product/WritePage.vue'),
        name: 'ProductAdd',
        meta: {
          title: '新增产品',
          noTagsView: true,
          noCache: true,
          hidden: true,
          canTo: true,
          activeMenu: '/product/list'
        }
      },
      {
        path: 'list/edit/:productId',
        component: () => import('@/views/Product/WritePage.vue'),
        name: 'ProductEdit',
        meta: {
          title: '编辑产品',
          noTagsView: true,
          noCache: true,
          hidden: true,
          canTo: true,
          activeMenu: '/product/list'
        }
      },
      {
        path: 'list/detail/:productId',
        component: () => import('@/views/Product/DetailPage.vue'),
        name: 'ProductDetail',
        meta: {
          title: '产品详情',
          noTagsView: true,
          noCache: true,
          hidden: true,
          canTo: true,
          activeMenu: '/product/list'
        }
      },
      {
        path: 'class/add',
        component: () => import('@/views/ProductClass/WritePage.vue'),
        name: 'ProductClassAdd',
        meta: {
          title: '新增产品分类',
          noTagsView: true,
          noCache: true,
          hidden: true,
          canTo: true,
          activeMenu: '/product/class'
        }
      },
      {
        path: 'class/edit/:classId',
        component: () => import('@/views/ProductClass/WritePage.vue'),
        name: 'ProductClassEdit',
        meta: {
          title: '编辑产品分类',
          noTagsView: true,
          noCache: true,
          hidden: true,
          canTo: true,
          activeMenu: '/product/class'
        }
      },
      {
        path: 'class/detail/:classId',
        component: () => import('@/views/ProductClass/DetailPage.vue'),
        name: 'ProductClassDetail',
        meta: {
          title: '产品分类详情',
          noTagsView: true,
          noCache: true,
          hidden: true,
          canTo: true,
          activeMenu: '/product/class'
        }
      },
      {
        path: 'attr/add',
        component: () => import('@/views/GoodsAttr/WritePage.vue'),
        name: 'GoodsAttrAdd',
        meta: {
          title: '新增商品属性',
          noTagsView: true,
          noCache: true,
          hidden: true,
          canTo: true,
          activeMenu: '/product/attr'
        }
      },
      {
        path: 'attr/edit/:attrId',
        component: () => import('@/views/GoodsAttr/WritePage.vue'),
        name: 'GoodsAttrEdit',
        meta: {
          title: '编辑商品属性',
          noTagsView: true,
          noCache: true,
          hidden: true,
          canTo: true,
          activeMenu: '/product/attr'
        }
      },
      {
        path: 'attr/detail/:attrId',
        component: () => import('@/views/GoodsAttr/DetailPage.vue'),
        name: 'GoodsAttrDetail',
        meta: {
          title: '商品属性详情',
          noTagsView: true,
          noCache: true,
          hidden: true,
          canTo: true,
          activeMenu: '/product/attr'
        }
      }
    ]
  },
  {
    path: '/operation',
    component: Layout,
    redirect: '/operation/region',
    name: 'OperationCenter',
    meta: {
      title: '运营设置',
      icon: 'vi-ep:promotion',
      alwaysShow: true
    },
    children: [
      {
        path: 'region',
        component: () => import('@/views/Region/Index.vue'),
        name: 'RegionManage',
        meta: {
          title: '区域管理'
        }
      },
      {
        path: 'region/dataset',
        component: () => import('@/views/Region/DatasetPage.vue'),
        name: 'RegionDatasetInfo',
        meta: {
          title: '区域字典信息',
          hidden: true,
          canTo: true,
          noCache: true,
          noTagsView: true,
          activeMenu: '/operation/region'
        }
      },
      {
        path: 'growth',
        alias: '/system/growth',
        component: getParentLayout(),
        redirect: '/operation/growth/level',
        name: 'GrowthCenter',
        meta: {
          title: '成长体系',
          alwaysShow: true
        },
        children: [
          {
            path: 'level',
            component: () => import('@/views/Growth/LevelConfig/Index.vue'),
            name: 'GrowthLevelConfig',
            meta: {
              title: '等级配置',
              activeMenu: '/operation/growth/level'
            }
          },
          {
            path: 'behavior',
            component: () => import('@/views/Growth/BehaviorConfig/Index.vue'),
            name: 'GrowthBehaviorConfig',
            meta: {
              title: '行为配置',
              activeMenu: '/operation/growth/behavior'
            }
          },
          {
            path: 'owner',
            redirect: '/operation/growth/user',
            name: 'GrowthOwnerManage',
            meta: { hidden: true, noTagsView: true }
          },
          {
            path: 'user',
            component: () => import('@/views/Growth/OwnerManage/UserPage.vue'),
            name: 'GrowthUserManage',
            meta: {
              title: '用户成长值管理',
              activeMenu: '/operation/growth/user'
            }
          },
          {
            path: 'company',
            component: () => import('@/views/Growth/OwnerManage/CompanyPage.vue'),
            name: 'GrowthCompanyManage',
            meta: {
              title: '企业成长值管理',
              activeMenu: '/operation/growth/company'
            }
          }
        ]
      },
      {
        path: 'business-role-type',
        alias: '/system/business-role-type',
        component: () => import('@/views/Company/BusinessRoleType/Index.vue'),
        name: 'CompanyBusinessRoleType',
        meta: {
          title: '经营身份类型',
          activeMenu: '/operation/business-role-type'
        }
      },
      {
        path: 'company-verify-config',
        alias: ['/company/verify-config', '/system/company-verify-config'],
        component: () => import('@/views/Company/VerifyConfig/Index.vue'),
        name: 'CompanyVerifyConfig',
        meta: {
          title: '企业认证配置',
          activeMenu: '/operation/company-verify-config'
        }
      }
    ]
  },
  {
    path: '/system',
    component: Layout,
    redirect: '/system/admin',
    name: 'System',
    meta: {
      title: '系统设置',
      icon: 'vi-ep:setting',
      alwaysShow: true
    },
    children: [
      {
        path: 'growth/level',
        redirect: '/operation/growth/level',
        name: 'GrowthLevelConfigRedirect',
        meta: { hidden: true, noTagsView: true }
      },
      {
        path: 'growth/behavior',
        redirect: '/operation/growth/behavior',
        name: 'GrowthBehaviorConfigRedirect',
        meta: { hidden: true, noTagsView: true }
      },
      {
        path: 'growth/owner',
        redirect: '/operation/growth/user',
        name: 'GrowthOwnerManageRedirect',
        meta: { hidden: true, noTagsView: true }
      },
      {
        path: 'admin',
        component: () => import('@/views/Authorization/User/User.vue'),
        name: 'User',
        meta: {
          noCache: true,
          title: '管理员账号'
        }
      },
      {
        path: 'admin/detail/:adminId',
        component: () => import('@/views/Authorization/User/DetailPage.vue'),
        name: 'AdminAccountDetail',
        meta: {
          title: '管理员详情',
          hidden: true,
          canTo: true,
          noCache: true,
          noTagsView: true,
          activeMenu: '/system/admin'
        }
      },
      {
        path: 'admin/edit/:adminId',
        component: () => import('@/views/Authorization/User/WritePage.vue'),
        name: 'AdminAccountEdit',
        meta: {
          title: '编辑管理员',
          hidden: true,
          canTo: true,
          noCache: true,
          noTagsView: true,
          activeMenu: '/system/admin'
        }
      },
      {
        path: 'server-monitor',
        component: () => import('@/views/ServerMonitor/Index.vue'),
        name: 'ServerMonitor',
        meta: {
          noCache: true,
          title: '服务器监控'
        }
      },
      {
        path: 'ai-prompts',
        component: () => import('@/views/SystemAiPrompts/Index.vue'),
        name: 'SystemAiPrompts',
        meta: {
          noCache: true,
          title: 'AI提示词管理'
        }
      },
      {
        path: 'markdown',
        component: () => import('@/views/SystemMarkdown/Index.vue'),
        name: 'SystemMarkdown',
        meta: {
          noCache: true,
          title: '文档管理'
        }
      },
      {
        path: 'ai-prompts/add',
        component: () => import('@/views/SystemAiPrompts/WritePage.vue'),
        name: 'SystemAiPromptsAdd',
        meta: {
          title: '新增提示词',
          noTagsView: true,
          noCache: true,
          hidden: true,
          canTo: true,
          activeMenu: '/system/ai-prompts'
        }
      },
      {
        path: 'ai-prompts/edit/:promptId',
        component: () => import('@/views/SystemAiPrompts/WritePage.vue'),
        name: 'SystemAiPromptsEdit',
        meta: {
          title: '编辑提示词',
          noTagsView: true,
          noCache: true,
          hidden: true,
          canTo: true,
          activeMenu: '/system/ai-prompts'
        }
      },
      {
        path: 'ai-prompts/detail/:promptId',
        component: () => import('@/views/SystemAiPrompts/DetailPage.vue'),
        name: 'SystemAiPromptsDetail',
        meta: {
          title: '提示词详情',
          noTagsView: true,
          noCache: true,
          hidden: true,
          canTo: true,
          activeMenu: '/system/ai-prompts'
        }
      },
      {
        path: 'markdown/add',
        component: () => import('@/views/SystemMarkdown/WritePage.vue'),
        name: 'SystemMarkdownAdd',
        meta: {
          title: '新增文档',
          noTagsView: true,
          noCache: true,
          hidden: true,
          canTo: true,
          activeMenu: '/system/markdown'
        }
      },
      {
        path: 'markdown/edit/:systemMarkdownId',
        component: () => import('@/views/SystemMarkdown/WritePage.vue'),
        name: 'SystemMarkdownEdit',
        meta: {
          title: '编辑文档',
          noTagsView: true,
          noCache: true,
          hidden: true,
          canTo: true,
          activeMenu: '/system/markdown'
        }
      },
      {
        path: 'markdown/detail/:systemMarkdownId',
        component: () => import('@/views/SystemMarkdown/DetailPage.vue'),
        name: 'SystemMarkdownDetail',
        meta: {
          title: '文档详情',
          noTagsView: true,
          noCache: true,
          hidden: true,
          canTo: true,
          activeMenu: '/system/markdown'
        }
      }
    ]
  },

  {
    path: '/external-link',
    component: Layout,
    meta: {},
    name: 'ExternalLink',
    children: [
      {
        path: 'https://element-plus-admin-doc.cn/',
        name: 'DocumentLink',
        meta: {
          title: t('router.document'),
          icon: 'vi-clarity:document-solid'
        }
      }
    ]
  },
  {
    path: '/guide',
    component: Layout,
    name: 'Guide',
    meta: {},
    children: [
      {
        path: 'index',
        component: () => import('@/views/Guide/Guide.vue'),
        name: 'GuideDemo',
        meta: {
          title: t('router.guide'),
          icon: 'vi-cib:telegram-plane'
        }
      }
    ]
  },
  {
    path: '/components',
    component: Layout,
    name: 'ComponentsDemo',
    meta: {
      title: t('router.component'),
      icon: 'vi-bx:bxs-component',
      alwaysShow: true
    },
    children: [
      {
        path: 'form',
        component: getParentLayout(),
        redirect: '/components/form/default-form',
        name: 'Form',
        meta: {
          title: t('router.form'),
          alwaysShow: true
        },
        children: [
          {
            path: 'default-form',
            component: () => import('@/views/Components/Form/DefaultForm.vue'),
            name: 'DefaultForm',
            meta: {
              title: t('router.defaultForm')
            }
          },
          {
            path: 'use-form',
            component: () => import('@/views/Components/Form/UseFormDemo.vue'),
            name: 'UseForm',
            meta: {
              title: 'UseForm'
            }
          }
        ]
      },
      {
        path: 'table',
        component: getParentLayout(),
        redirect: '/components/table/default-table',
        name: 'TableDemo',
        meta: {
          title: t('router.table'),
          alwaysShow: true
        },
        children: [
          {
            path: 'default-table',
            component: () => import('@/views/Components/Table/DefaultTable.vue'),
            name: 'DefaultTable',
            meta: {
              title: t('router.defaultTable')
            }
          },
          {
            path: 'use-table',
            component: () => import('@/views/Components/Table/UseTableDemo.vue'),
            name: 'UseTable',
            meta: {
              title: 'UseTable'
            }
          },
          {
            path: 'tree-table',
            component: () => import('@/views/Components/Table/TreeTable.vue'),
            name: 'TreeTable',
            meta: {
              title: t('router.treeTable')
            }
          },
          {
            path: 'table-image-preview',
            component: () => import('@/views/Components/Table/TableImagePreview.vue'),
            name: 'TableImagePreview',
            meta: {
              title: t('router.PicturePreview')
            }
          },
          {
            path: 'table-video-preview',
            component: () => import('@/views/Components/Table/TableVideoPreview.vue'),
            name: 'TableVideoPreview',
            meta: {
              title: t('router.tableVideoPreview')
            }
          },
          {
            path: 'card-table',
            component: () => import('@/views/Components/Table/CardTable.vue'),
            name: 'CardTable',
            meta: {
              title: t('router.cardTable')
            }
          }
        ]
      },
      {
        path: 'editor-demo',
        component: getParentLayout(),
        redirect: '/components/editor-demo/editor',
        name: 'EditorDemo',
        meta: {
          title: t('router.editor'),
          alwaysShow: true
        },
        children: [
          {
            path: 'editor',
            component: () => import('@/views/Components/Editor/Editor.vue'),
            name: 'Editor',
            meta: {
              title: t('router.richText')
            }
          },
          {
            path: 'json-editor',
            component: () => import('@/views/Components/Editor/JsonEditor.vue'),
            name: 'JsonEditor',
            meta: {
              title: t('router.jsonEditor')
            }
          }
        ]
      },
      {
        path: 'search',
        component: () => import('@/views/Components/Search.vue'),
        name: 'Search',
        meta: {
          title: t('router.search')
        }
      },
      {
        path: 'descriptions',
        component: () => import('@/views/Components/Descriptions.vue'),
        name: 'Descriptions',
        meta: {
          title: t('router.descriptions')
        }
      },
      {
        path: 'image-viewer',
        component: () => import('@/views/Components/ImageViewer.vue'),
        name: 'ImageViewer',
        meta: {
          title: t('router.imageViewer')
        }
      },
      {
        path: 'dialog',
        component: () => import('@/views/Components/Dialog.vue'),
        name: 'Dialog',
        meta: {
          title: t('router.dialog')
        }
      },
      {
        path: 'icon',
        component: () => import('@/views/Components/Icon.vue'),
        name: 'Icon',
        meta: {
          title: t('router.icon')
        }
      },
      {
        path: 'icon-picker',
        component: () => import('@/views/Components/IconPicker.vue'),
        name: 'IconPicker',
        meta: {
          title: t('router.iconPicker')
        }
      },
      {
        path: 'echart',
        component: () => import('@/views/Components/Echart.vue'),
        name: 'Echart',
        meta: {
          title: t('router.echart')
        }
      },
      {
        path: 'count-to',
        component: () => import('@/views/Components/CountTo.vue'),
        name: 'CountTo',
        meta: {
          title: t('router.countTo')
        }
      },
      {
        path: 'qrcode',
        component: () => import('@/views/Components/Qrcode.vue'),
        name: 'Qrcode',
        meta: {
          title: t('router.qrcode')
        }
      },
      {
        path: 'highlight',
        component: () => import('@/views/Components/Highlight.vue'),
        name: 'Highlight',
        meta: {
          title: t('router.highlight')
        }
      },
      {
        path: 'infotip',
        component: () => import('@/views/Components/Infotip.vue'),
        name: 'Infotip',
        meta: {
          title: t('router.infotip')
        }
      },
      {
        path: 'input-password',
        component: () => import('@/views/Components/InputPassword.vue'),
        name: 'InputPassword',
        meta: {
          title: t('router.inputPassword')
        }
      },
      {
        path: 'waterfall',
        component: () => import('@/views/Components/Waterfall.vue'),
        name: 'waterfall',
        meta: {
          title: t('router.waterfall')
        }
      },
      {
        path: 'image-cropping',
        component: () => import('@/views/Components/ImageCropping.vue'),
        name: 'ImageCropping',
        meta: {
          title: t('router.imageCropping')
        }
      },
      {
        path: 'video-player',
        component: () => import('@/views/Components/VideoPlayer.vue'),
        name: 'VideoPlayer',
        meta: {
          title: t('router.videoPlayer')
        }
      },
      {
        path: 'avatars',
        component: () => import('@/views/Components/Avatars.vue'),
        name: 'Avatars',
        meta: {
          title: t('router.avatars')
        }
      },
      {
        path: 'i-agree',
        component: () => import('@/views/Components/IAgree.vue'),
        name: 'IAgree',
        meta: {
          title: t('router.iAgree')
        }
      }
    ]
  },
  {
    path: '/function',
    component: Layout,
    redirect: '/function/multipleTabs',
    name: 'Function',
    meta: {
      title: t('router.function'),
      icon: 'vi-ri:function-fill',
      alwaysShow: true
    },
    children: [
      {
        path: 'multiple-tabs',
        component: () => import('@/views/Function/MultipleTabs.vue'),
        name: 'MultipleTabs',
        meta: {
          title: t('router.multipleTabs')
        }
      },
      {
        path: 'multiple-tabs-demo/:id',
        component: () => import('@/views/Function/MultipleTabsDemo.vue'),
        name: 'MultipleTabsDemo',
        meta: {
          hidden: true,
          title: t('router.details'),
          canTo: true,
          activeMenu: '/function/multiple-tabs'
        }
      },
      {
        path: 'request',
        component: () => import('@/views/Function/Request.vue'),
        name: 'Request',
        meta: {
          title: t('router.request')
        }
      },
      {
        path: 'test',
        component: () => import('@/views/Function/Test.vue'),
        name: 'Test',
        meta: {
          title: t('router.permission'),
          permission: ['add', 'edit', 'delete']
        }
      }
    ]
  },
  {
    path: '/hooks',
    component: Layout,
    redirect: '/hooks/useWatermark',
    name: 'Hooks',
    meta: {
      title: 'hooks',
      icon: 'vi-ic:outline-webhook',
      alwaysShow: true
    },
    children: [
      {
        path: 'useWatermark',
        component: () => import('@/views/hooks/useWatermark.vue'),
        name: 'UseWatermark',
        meta: {
          title: 'useWatermark'
        }
      },
      {
        path: 'useTagsView',
        component: () => import('@/views/hooks/useTagsView.vue'),
        name: 'UseTagsView',
        meta: {
          title: 'useTagsView'
        }
      },
      {
        path: 'useValidator',
        component: () => import('@/views/hooks/useValidator.vue'),
        name: 'UseValidator',
        meta: {
          title: 'useValidator'
        }
      },
      {
        path: 'useCrudSchemas',
        component: () => import('@/views/hooks/useCrudSchemas.vue'),
        name: 'UseCrudSchemas',
        meta: {
          title: 'useCrudSchemas'
        }
      },
      {
        path: 'useClipboard',
        component: () => import('@/views/hooks/useClipboard.vue'),
        name: 'UseClipboard',
        meta: {
          title: 'useClipboard'
        }
      },
      {
        path: 'useNetwork',
        component: () => import('@/views/hooks/useNetwork.vue'),
        name: 'UseNetwork',
        meta: {
          title: 'useNetwork'
        }
      }
    ]
  },
  {
    path: '/level',
    component: Layout,
    redirect: '/level/menu1/menu1-1/menu1-1-1',
    name: 'Level',
    meta: {
      title: t('router.level'),
      icon: 'vi-carbon:skill-level-advanced'
    },
    children: [
      {
        path: 'menu1',
        name: 'Menu1',
        component: getParentLayout(),
        redirect: '/level/menu1/menu1-1/menu1-1-1',
        meta: {
          title: t('router.menu1')
        },
        children: [
          {
            path: 'menu1-1',
            name: 'Menu11',
            component: getParentLayout(),
            redirect: '/level/menu1/menu1-1/menu1-1-1',
            meta: {
              title: t('router.menu11'),
              alwaysShow: true
            },
            children: [
              {
                path: 'menu1-1-1',
                name: 'Menu111',
                component: () => import('@/views/Level/Menu111.vue'),
                meta: {
                  title: t('router.menu111')
                }
              }
            ]
          },
          {
            path: 'menu1-2',
            name: 'Menu12',
            component: () => import('@/views/Level/Menu12.vue'),
            meta: {
              title: t('router.menu12')
            }
          }
        ]
      },
      {
        path: 'menu2',
        name: 'Menu2',
        component: () => import('@/views/Level/Menu2.vue'),
        meta: {
          title: t('router.menu2')
        }
      }
    ]
  },
  {
    path: '/example',
    component: Layout,
    redirect: '/example/example-dialog',
    name: 'Example',
    meta: {
      title: t('router.example'),
      icon: 'vi-ep:management',
      alwaysShow: true
    },
    children: [
      {
        path: 'example-dialog',
        component: () => import('@/views/Example/Dialog/ExampleDialog.vue'),
        name: 'ExampleDialog',
        meta: {
          title: t('router.exampleDialog')
        }
      },
      {
        path: 'example-page',
        component: () => import('@/views/Example/Page/ExamplePage.vue'),
        name: 'ExamplePage',
        meta: {
          title: t('router.examplePage')
        }
      },
      {
        path: 'example-add',
        component: () => import('@/views/Example/Page/ExampleAdd.vue'),
        name: 'ExampleAdd',
        meta: {
          title: t('router.exampleAdd'),
          noTagsView: true,
          noCache: true,
          hidden: true,
          canTo: true,
          activeMenu: '/example/example-page'
        }
      },
      {
        path: 'example-edit',
        component: () => import('@/views/Example/Page/ExampleEdit.vue'),
        name: 'ExampleEdit',
        meta: {
          title: t('router.exampleEdit'),
          noTagsView: true,
          noCache: true,
          hidden: true,
          canTo: true,
          activeMenu: '/example/example-page'
        }
      },
      {
        path: 'example-detail',
        component: () => import('@/views/Example/Page/ExampleDetail.vue'),
        name: 'ExampleDetail',
        meta: {
          title: t('router.exampleDetail'),
          noTagsView: true,
          noCache: true,
          hidden: true,
          canTo: true,
          activeMenu: '/example/example-page'
        }
      }
    ]
  },
  {
    path: '/error',
    component: Layout,
    redirect: '/error/404',
    name: 'Error',
    meta: {
      title: t('router.errorPage'),
      icon: 'vi-ci:error',
      alwaysShow: true
    },
    children: [
      {
        path: '404-demo',
        component: () => import('@/views/Error/404.vue'),
        name: '404Demo',
        meta: {
          title: '404'
        }
      },
      {
        path: '403-demo',
        component: () => import('@/views/Error/403.vue'),
        name: '403Demo',
        meta: {
          title: '403'
        }
      },
      {
        path: '500-demo',
        component: () => import('@/views/Error/500.vue'),
        name: '500Demo',
        meta: {
          title: '500'
        }
      }
    ]
  },
  {
    path: '/authorization',
    component: Layout,
    redirect: '/authorization/department',
    name: 'Authorization',
    meta: {
      title: t('router.authorization'),
      icon: 'vi-eos-icons:role-binding',
      alwaysShow: true
    },
    children: [
      {
        path: 'department',
        component: () => import('@/views/Authorization/Department/Department.vue'),
        name: 'Department',
        meta: {
          title: t('router.department')
        }
      },
      {
        path: 'menu',
        component: () => import('@/views/Authorization/Menu/Menu.vue'),
        name: 'Menu',
        meta: {
          title: t('router.menuManagement')
        }
      },
      {
        path: 'role',
        component: () => import('@/views/Authorization/Role/Role.vue'),
        name: 'Role',
        meta: {
          title: t('router.role')
        }
      }
    ]
  }
]

const router = createRouter({
  history: createWebHashHistory(),
  strict: true,
  routes: constantRouterMap as RouteRecordRaw[],
  scrollBehavior: () => ({ left: 0, top: 0 })
})

export const resetRouter = (): void => {
  router.getRoutes().forEach((route) => {
    const { name } = route
    if (name && !NO_RESET_WHITE_LIST.includes(name as string)) {
      router.hasRoute(name) && router.removeRoute(name)
    }
  })
}

export const setupRouter = (app: App<Element>) => {
  app.use(router)
}

export default router
